import React, { useEffect, useState } from "react";
import { Navigate, Outlet, Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Modal from "../../components/generics/Modal";
import UIIcon from "../../components/generics/UIIcon";
import RefinedUIProvider from "../../components/generics/RefinedUIProvider";
import {
  isLoggedIn,
  isServiceProvider,
  getServiceProviderData,
  getUserData,
  logout,
} from "../../utils/AuthService";
import { withRouter } from "utils/WithRouter";
import useRoutePrefetch from "../../hooks/useRoutePrefetch";
import useRefinedRouteScrollReset from "../../hooks/useRefinedRouteScrollReset";
import { canUseRefinedAppUI } from "../../utils/featureFlags";
import { getRefinedUIRouteContext } from "../../utils/refinedUIRoutes";

const ProviderLayout = ({ history }) => {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const location = useLocation();
  const prefetchRootRef = useRoutePrefetch(location.pathname);
  const providerData = getServiceProviderData();
  // This portal does not mount the shared Sidebar that loads the Redux user.
  // Reuse the existing login profile until that user is available.
  const refinedUI = canUseRefinedAppUI(currentUser?.user ? currentUser : getUserData());
  useRefinedRouteScrollReset(location.pathname, refinedUI);
  const routeContext = getRefinedUIRouteContext(location.pathname);
  const refinedContentClass = refinedUI
    ? ` refined-ui-content ${routeContext.className}`
    : "";

  useEffect(() => {
    if (!refinedUI) return undefined;
    return () => document.body.classList.remove("sidebar-mobile-show");
  }, [refinedUI]);

  // If not logged in, redirect to login
  if (!isLoggedIn()) {
    return <Navigate to="/login" />;
  }

  // If logged in but NOT a service provider, redirect to main app
  if (!isServiceProvider()) {
    return <Navigate to="/orders" />;
  }

  const handleLogout = () => {
    logout();
    history.push("/login");
  };

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const closeMobileNavigation = () => {
    document.body.classList.remove("sidebar-mobile-show");
    setMobileNavigationOpen(false);
  };

  const toggleMobileNavigation = () => {
    const isOpen = document.body.classList.toggle("sidebar-mobile-show");
    setMobileNavigationOpen(isOpen);
  };

  return (
    <RefinedUIProvider enabled={refinedUI}>
      <div ref={prefetchRootRef} className={`app${refinedUI ? " app--refined" : ""} provider-portal-layout`}
        onKeyDown={refinedUI ? (event) => {
          if (event.key === "Escape" && mobileNavigationOpen) closeMobileNavigation();
        } : undefined}>
        <header className="app-header navbar">
          {refinedUI && (
            <button className="navbar-toggler mobile-sidebar-toggler hidden-lg-up" type="button"
              aria-label="Menu" aria-expanded={mobileNavigationOpen} aria-controls="provider-navigation"
              onClick={toggleMobileNavigation}>
              <UIIcon name="menu" size={20} />
            </button>
          )}
          <Link className="navbar-brand" to="/provider-portal" aria-label={refinedUI ? "Graziano's Service Portal" : undefined}
            style={refinedUI ? undefined : { backgroundImage: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span className={refinedUI ? "sr-only" : undefined}
              style={refinedUI ? undefined : { color: "#fff", fontSize: "14px", fontWeight: "500" }}>Service Portal</span>
          </Link>
          <ul className="nav navbar-nav hidden-md-down">
            <li className="nav-item px-3">
              <Link
                to="/provider-portal"
                className={`nav-link ${isActive("/provider-portal", true) ? "active" : ""}`}
              >
                {refinedUI ? <UIIcon name="inventory" /> : <i className="fa fa-dashboard me-1"></i>} Dashboard
              </Link>
            </li>
            <li className="nav-item px-3">
              <Link
                to="/provider-portal/tasks"
                className={`nav-link ${isActive("/provider-portal/tasks") ? "active" : ""}`}
              >
                {refinedUI ? <UIIcon name="audit" /> : <i className="fa fa-tasks me-1"></i>} My Tasks
              </Link>
            </li>
          </ul>
          <ul className="nav navbar-nav ml-auto">
            <li className="nav-item px-3 hidden-md-down">
              <span className="nav-link" style={{ cursor: "default" }}>
                <i className="fa fa-building me-1"></i>
                {providerData?.name || "Service Provider"}
              </span>
            </li>
            <li className="nav-item dropdown resp-pr-15">
              <a
                className="nav-link resp-logout"
                href="#"
                role="button"
                aria-label={refinedUI ? "Logout" : undefined}
                onClick={(e) => { e.preventDefault(); setShowLogoutModal(true); }}
              >
                {refinedUI ? <UIIcon name="logout" /> : <i className="fa fa-sign-out"></i>} <span className="hidden-xxs-down">Logout</span>
              </a>
            </li>
          </ul>
        </header>
        <div className="app-body">
          <div className="sidebar" style={{ width: "220px" }}>
            <nav className="sidebar-nav" id="provider-navigation" aria-label={refinedUI ? "Service portal navigation" : undefined}>
              <ul className="nav">
                <li className="nav-item">
                  <Link
                    to="/provider-portal"
                    onClick={refinedUI ? closeMobileNavigation : undefined}
                    className={`nav-link ${isActive("/provider-portal", true) ? "active" : ""}`}
                  >
                    {refinedUI ? <UIIcon name="inventory" /> : <i className="fa fa-dashboard"></i>} Dashboard
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/provider-portal/tasks"
                    onClick={refinedUI ? closeMobileNavigation : undefined}
                    className={`nav-link ${isActive("/provider-portal/tasks") ? "active" : ""}`}
                  >
                    {refinedUI ? <UIIcon name="audit" /> : <i className="fa fa-tasks"></i>} My Tasks
                  </Link>
                </li>
                <li className="nav-title" style={{ padding: "0.75rem 1rem", color: "#73818f", fontSize: "80%", fontWeight: "700", textTransform: "uppercase" }}>
                  Quick Filters
                </li>
                <li className="nav-item">
                  <Link
                    to="/provider-portal/tasks?status=PENDING"
                    onClick={refinedUI ? closeMobileNavigation : undefined}
                    className="nav-link"
                  >
                    <i className="fa fa-clock-o"></i> Pending
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/provider-portal/tasks?status=IN_PROGRESS"
                    onClick={refinedUI ? closeMobileNavigation : undefined}
                    className="nav-link"
                  >
                    <i className="fa fa-spinner"></i> In Progress
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/provider-portal/tasks?status=OVERDUE"
                    onClick={refinedUI ? closeMobileNavigation : undefined}
                    className="nav-link"
                  >
                    <i className="fa fa-exclamation-triangle"></i> Overdue
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
          <main className="main">
            <div className={`container-fluid mt-2${refinedContentClass}`}>
              <Outlet />
            </div>
          </main>
        </div>
        <footer className="app-footer">
          <a href="http://www.grazianosgroup.com/">Grazianos</a> &copy; 2025 - Service Provider Portal
        </footer>
        <Modal
          className={refinedUI ? "app-ui-modal--refined" : undefined}
          closeModal={() => setShowLogoutModal(false)}
          isModalOpen={showLogoutModal}
          onConfirm={handleLogout}
          onCancel={() => setShowLogoutModal(false)}
          header="LOG OUT"
          content="Are you sure you want to log out?"
          onConfirmButtonText="Yes"
          onCancelButtonText="No"
        />
      </div>
    </RefinedUIProvider>
  );
};

export default withRouter(ProviderLayout);
